import { CSSProperties } from "react";
import {
  BrandKit,
  ButtonStyleDefinition,
  Enableable,
  fontWeightValues,
  InputStyleDefinition,
  Override,
  styleFromFontWeight,
} from "../brand-kit";
import {
  ButtonHierarchy,
  ButtonStyleOverride,
  InputStyleOverride,
} from "../element-schema";
import { getFontFamilyWithFallback } from "./font-utils";

export function getButtonStyles({
  brandKit,
  hierarchy,
  globalOverride: _globalOverride,
  elementOverride,
  fullWidth = true,
}: {
  brandKit: BrandKit;
  hierarchy: ButtonHierarchy;
  globalOverride?:
    | Override<ButtonStyleDefinition>
    | Enableable<Override<ButtonStyleDefinition>>;
  elementOverride?: ButtonStyleOverride | null;
  fullWidth?: boolean;
}) {
  const globalOverride =
    _globalOverride && "enabled" in _globalOverride && !_globalOverride.enabled
      ? undefined
      : _globalOverride;
  const styles = globalOverride ? globalOverride : brandKit?.buttons[hierarchy];

  const backgroundColor =
    elementOverride?.backgroundColor ?? styles.backgroundColor ?? undefined;
  const textColor = elementOverride?.textColor ?? styles.textColor;

  const borderStrokeWeightPx =
    elementOverride?.border?.stroke.weightPx ?? styles.border.stroke.weightPx;
  const borderStrokeColor =
    elementOverride?.border?.stroke.color ?? styles.border.stroke.color;
  const borderCornerRadiusPx =
    elementOverride?.border?.cornerRadiusPx ?? styles.border.cornerRadiusPx;

  const fontFamily = getFontFamilyWithFallback(
    elementOverride?.font?.fontFamily ??
      ("fontReference" in styles
        ? brandKit?.font.bodyFontFamily
        : styles.font.fontFamily),
    brandKit?.customFontFamilies,
  );
  const fontSizePx =
    elementOverride?.font?.fontSizePx ??
    ("fontReference" in styles
      ? brandKit?.font.hierarchy[styles.fontReference].fontSizePx
      : styles.font.fontSizePx);
  const fontWeight = styleFromFontWeight(
    elementOverride?.font?.fontWeight ??
      ("fontReference" in styles
        ? brandKit?.font.hierarchy[styles.fontReference].fontWeight
        : styles.font.fontWeight),
  );

  const padding = elementOverride?.padding
    ? `${elementOverride.padding.top}px ${elementOverride.padding.right}px ${elementOverride.padding.bottom}px ${elementOverride.padding.left}px`
    : `${styles.paddingPx}px`;

  return {
    outline: "0px",
    backgroundColor,
    color: textColor,
    border: `${borderStrokeWeightPx}px solid ${borderStrokeColor}`,
    borderRadius: `${borderCornerRadiusPx}px`,
    width: fullWidth ? "100%" : "auto",
    fontFamily,
    fontSize: `${fontSizePx}px`,
    fontWeight,
    padding,
    cursor: "pointer",
  } satisfies CSSProperties;
}

export function getInputStyles(
  brandKit: BrandKit,
  _globalOverride?:
    | Override<InputStyleDefinition>
    | Enableable<Override<InputStyleDefinition>>,
  elementOverride?: InputStyleOverride | null,
): React.CSSProperties {
  const globalOverride =
    _globalOverride && "enabled" in _globalOverride && !_globalOverride.enabled
      ? undefined
      : _globalOverride;
  const styles = globalOverride ?? brandKit?.inputs;

  const backgroundColor =
    elementOverride?.backgroundColor ?? styles.backgroundColor ?? undefined;
  const textColor = elementOverride?.textColor ?? styles.textColor;
  const borderStrokeWeightPx =
    elementOverride?.border?.stroke.weightPx ?? styles.border.stroke.weightPx;
  const borderStrokeColor =
    elementOverride?.border?.stroke.color ?? styles.border.stroke.color;
  const borderCornerRadiusPx =
    elementOverride?.border?.cornerRadiusPx ?? styles.border.cornerRadiusPx;

  const fontFamily = getFontFamilyWithFallback(
    elementOverride?.font?.fontFamily ??
      ("fontReference" in styles
        ? brandKit?.font.bodyFontFamily
        : styles.font.fontFamily),
    brandKit?.customFontFamilies,
  );
  const fontSizePx =
    elementOverride?.font?.fontSizePx ??
    ("fontReference" in styles
      ? brandKit?.font.hierarchy[styles.fontReference].fontSizePx
      : styles.font.fontSizePx);
  const fontWeight =
    fontWeightValues[
      elementOverride?.font?.fontWeight ??
        ("fontReference" in styles
          ? brandKit?.font.hierarchy[styles.fontReference].fontWeight
          : styles.font.fontWeight)
    ];

  const padding = elementOverride?.padding
    ? `${elementOverride.padding.top}px ${elementOverride.padding.right}px ${elementOverride.padding.bottom}px ${elementOverride.padding.left}px`
    : `${styles.paddingPx}px`;

  return {
    backgroundColor,
    color: textColor,
    borderWidth: `${borderStrokeWeightPx}px`,
    borderStyle: "solid",
    borderColor: borderStrokeColor,
    width: "100%",
    borderRadius: `${borderCornerRadiusPx}px`,
    fontFamily,
    fontSize: `${fontSizePx}px`,
    fontWeight,
    padding,
  };
}
